import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CurrentUserSelectors } from '@recruitee/account-data';
import type { TrackingActivity } from '@recruitee/activities-types';
import { filterBoolean } from '@recruitee/common';
import { AppNavigatorService } from '@recruitee/navigation';

@Injectable({ providedIn: 'root' })
export class ActivityRedirectService {
  private currentAdminId: number;
  private ngOnDestroy$ = new Subject<void>();

  private readonly requisitionActivitiesListByAssociation = {
    approvalFlow: ['requisition_approvers_changed', 'requisition_approval_flow_changed'],
    teamMembers: [
      'requisition_follower_add',
      'requisition_follower_remove',
      'requisition_assignment_add',
      'requisition_assignment_remove',
      'requisition_recruiter_change',
      'requisition_recruiter_clear',
      'requisition_hiring_manager_change',
      'requisition_hiring_manager_clear',
    ],
    notes: [
      'requisition_note_add',
      'requisition_note_remove',
      'note_reply',
      'note_mention',
      'note_reaction',
    ],
    jobs: ['requisition_job_added', 'requisition_assigned', 'requisition_unassigned'],
  };

  private readonly textingActivitiesList = {
    settings: [
      'texting_enabled',
      'texting_disabled',
      'texting_phone_number_registered',
      'texting_phone_number_deleted',
      'texting_phone_number_removed_by_system',
      'texting_campaign_removed_by_system',
    ],
    candidate: [
      'texting_consent_added',
      'texting_consent_withdrawn',
      'texting_consent_granted_by_candidate',
      'texting_consent_withdrawn_by_candidate',
      'texting_message_sent',
      'texting_message_sent_by_candidate',
    ],
  };

  private readonly locationsActivitiesList = {
    settings: [
      'location_archived',
      'location_created',
      'location_deleted',
      'location_retrieved',
      'location_updated',
    ],
  };

  constructor(
    private router: Router,
    private store: Store,
    private appNavigator: AppNavigatorService,
  ) {
    store
      .select(CurrentUserSelectors.data)
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(admin => (this.currentAdminId = admin.id));
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  // eslint-disable-next-line max-lines-per-function
  public redirect(activity: TrackingActivity): void {
    if (this.textingActivitiesList.settings.includes(activity.event)) {
      this.router.navigateByUrl('/settings/texting');
      return;
    }
    if (this.textingActivitiesList.candidate.includes(activity.event)) {
      this.appNavigator.openCandidateDialog(activity.candidate!.id, {
        tab: 'messages',
        messagesMode: 'sms',
      });
      return;
    }
    if (this.locationsActivitiesList.settings.includes(activity.event)) {
      this.router.navigateByUrl('/settings/locations');
      return;
    }

    if (activity.mailboxMessage) {
      this.router.navigateByUrl(`/mailbox/my/all/${activity.mailboxMessage.threadId}`);
    } else if (
      ['task_add', 'task_complete', 'task_overdue', 'task_due_today'].includes(activity.event)
    ) {
      this.router.navigateByUrl(
        'dashboard/tasks?resetFilters=true' + (activity.task ? `&taskId=${activity.task.id}` : ''),
      );
    } else if (!activity.candidate && !activity.offer && activity.event === 'assignment_add') {
      this.router.navigateByUrl('dashboard/tasks');
    } else if (activity.event === 'disqualify_reason_remove') {
      this.router.navigateByUrl('/settings/disqualify_reasons');
    } else if (['share_container_add', 'share_container_remove'].includes(activity.event)) {
      this.router.navigateByUrl('/settings/containers');
    } else if (activity.event === 'stage_remove') {
      this.router.navigateByUrl('/settings/pipeline');
    } else if (activity.event === 'tag_remove') {
      this.router.navigateByUrl('/settings/tags');
    } else if (activity.event === 'job_remove') {
      this.router.navigateByUrl(activity.offer!.kind === 'job' ? `/offers` : `/talent_pools`);
    } else if (['requisition_disabled', 'requisition_enabled'].includes(activity.event)) {
      this.router.navigateByUrl('/settings/requisition_approvals/setup');
    } else if (
      [
        'requisition_approval_flow_create',
        'requisition_approval_flow_update',
        'requisition_approval_flow_remove',
      ].includes(activity.event)
    ) {
      this.router.navigateByUrl('/settings/requisition_approvals/flows');
    } else if (activity.event === 'requisition_form_updated') {
      this.router.navigateByUrl('/settings/requisition_approvals/form');
    } else if (
      activity.offer &&
      ['job_note_add', 'job_note_remove', 'note_mention'].includes(activity.event)
    ) {
      this.router.navigateByUrl(
        activity.offer.kind === 'job'
          ? `/offers/${activity.offer.slug}/notes`
          : `/talent_pools/${activity.offer.slug}/notes`,
      );
    } else if (activity.offer && ['job_tags', 'job_tags_remove'].includes(activity.event)) {
      this.router.navigateByUrl(
        activity.offer.kind === 'job'
          ? `/offers/${activity.offer.slug}/edit`
          : `/talent_pools/${activity.offer.slug}/edit`,
      );
    } else if (activity.event === 'job_overdue_candidates' && activity.offer) {
      this.router.navigateByUrl(`/offers/${activity.offer.slug}/filters?overdue=true`);
    } else if (activity.event === 'candidate_overdue') {
      this.router.navigateByUrl(`/candidates?overdue=true&has_follower=${this.currentAdminId}`);
    } else if (
      activity.requisition &&
      this.requisitionActivitiesListByAssociation.approvalFlow.includes(activity.event)
    ) {
      this.router.navigateByUrl(`/requisitions/${activity.requisition.id}/flow`);
    } else if (
      activity.requisition &&
      this.requisitionActivitiesListByAssociation.jobs.includes(activity.event)
    ) {
      this.router.navigateByUrl(`/requisitions/${activity.requisition.id}/jobs`);
    } else if (
      activity.requisition &&
      this.requisitionActivitiesListByAssociation.notes.includes(activity.event)
    ) {
      this.router.navigateByUrl(`/requisitions/${activity.requisition.id}/notes`);
    } else if (
      activity.requisition &&
      ['requisition_attachment_upload', 'requisition_attachment_remove'].includes(activity.event)
    ) {
      this.router.navigateByUrl(`/requisitions/${activity.requisition.id}/files`);
    } else if (
      activity.requisition &&
      this.requisitionActivitiesListByAssociation.teamMembers.includes(activity.event)
    ) {
      this.router.navigateByUrl(`/requisitions/${activity.requisition.id}/team`);
    } else if (
      activity.requisition &&
      !activity.offer &&
      !activity.candidate &&
      !['requisition_deleted'].includes(activity.event)
    ) {
      this.router.navigateByUrl(`/requisitions/${activity.requisition.id}/details`);
    } else if (!activity.candidate && activity.offer) {
      this.router.navigateByUrl(
        activity.offer.kind === 'job'
          ? `/offers/${activity.offer.slug}/pipeline`
          : `/talent_pools/${activity.offer.slug}/filters`,
      );
    } else if (activity.import) {
      this.router.navigateByUrl(
        activity.import.sourceId
          ? `/candidates?has_sources=${activity.import.sourceId}`
          : '/candidates?sort=created_at_desc',
      );
    } else if (activity.candidate) {
      this.router.navigate([], {
        queryParams: { candidate: activity.candidate.id },
        queryParamsHandling: 'merge',
      });
    } else if (activity.event === 'referral_reward_claimed') {
      this.router.navigateByUrl('/settings/referrals/claims');
    }
  }
}
